import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { cx } from '../../styles';
import SessionList from '../common/SessionList';
import { getStoredSessions, deleteSession, Session } from '../../utils/sessionStorage';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isMobile: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, isMobile }) => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<Session[]>([]);
  const currentSessionId = new URLSearchParams(window.location.search).get('sessionId');
  // Load sessions when component mounts
  useEffect(() => {
    setSessions(getStoredSessions());
  }, [currentSessionId]);
  
  // Session management handlers
  const handleSessionSelect = (sessionId: string) => {
    navigate(`/chat?sessionId=${sessionId}`);
    if (isMobile) onClose();
  };
  
  const handleDeleteSession = (sessionId: string) => {
    deleteSession(sessionId);
    setSessions(prevSessions => prevSessions.filter(s => s.id !== sessionId));
    const currentSessionId = new URLSearchParams(window.location.search).get('sessionId');
    if (currentSessionId === sessionId) {
      navigate('/');
    }
  };

  // Track animation state
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationClass, setAnimationClass] = useState('');
  
  // Apply appropriate animation when isOpen changes
  useEffect(() => {
    if (isMobile) {
      setIsAnimating(true);
      setAnimationClass(isOpen ? 'animate-slide-in' : 'animate-slide-out');
      
      // After animation completes, reset animating state
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 300); // Match animation duration
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, isMobile]);
  
  // Don't render anything if mobile, sidebar is closed, and not animating
  if (isMobile && !isOpen && !isAnimating) {
    return null;
  }
  
  return (
    <>
      {/* Mobile overlay - animated fade in/out */}
      {isMobile && (isOpen || isAnimating) && (
        <div 
          className={`fixed inset-0 bg-black bg-opacity-50 z-40 ${isOpen ? 'animate-fade-in' : 'animate-fade-out'}`}
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <div 
        className={cx(
          "h-screen overflow-y-auto",
          "flex flex-col",
          "bg-component",
          "border-r border-divider",
          isMobile ? "fixed left-0 top-0 z-50 w-64" : "w-64",
          isMobile && !isOpen && !isAnimating ? "hidden" : "",
          isMobile && animationClass
        )}
        onAnimationEnd={() => {
          // Clean up after animation ends
          if (!isOpen) {
            setAnimationClass('');
          }
        }}
      >
        {/* Logo and close button (mobile only) */}
        <div className="p-4 flex items-center justify-between border-b border-divider">
          <Link to="/" className="text-xl font-bold text-primary">zkML Research</Link>
          {isMobile && (
            <button 
              onClick={onClose}
              className="p-1 rounded-full hover:bg-hover text-tertiary"
              aria-label="Close sidebar"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        
        {/* Home Navigation */}
        <div className="p-4 pb-2">
          <Link 
            to="/" 
            className={cx(
              "flex items-center p-2 rounded-lg w-full",
              "hover:bg-hover text-primary",
              "transition-colors"
            )}
            onClick={isMobile ? onClose : undefined}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Home
          </Link>
        </div>
        
        {/* Chat Sessions */}
        <div className="flex-1 overflow-hidden p-4 pt-2">
          <SessionList
            sessions={sessions}
            currentSessionId={new URLSearchParams(window.location.search).get('sessionId')}
            onSessionSelect={handleSessionSelect}
            onDeleteSession={handleDeleteSession}
          />
        </div>
        
        <div className="mt-auto p-4 border-t border-divider">
          <div className="text-sm text-tertiary">
            Powered by zkML Research
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
